import { Box, Tabs, Tab, TextField, FormControlLabel, Checkbox } from '@mui/material';
import { useEffect, useState } from 'react';
import { EventTable } from '../../components/EventTable';
import { PageTitle } from '../../components/PageTitle';
import { AnimatedBox } from '../../components/AnimatedBox';
import { EmptyState } from '../../components/EmptyState';
import { LoadingState } from '../../components/LoadingState';
import { AddEventButton } from '../../components/AddEventButton';
import EventDialog from '../../components/EventDialog';
import { Enterprise, ENTERPRISE_COLLECTION, EventSignUp, User, USER_COLLECTION, type Event } from '../../type';
import { DialogMode } from '../../type/DialogMode';
import { collection, getFirestore, onSnapshot, orderBy, query, Timestamp } from 'firebase/firestore';
import { EventFilter } from '../../components/EventFilter';
import { EventSummaryTable } from '../../components/EventSummaryTable';
import { ExportExcelButton } from '../../components/ExportExcelButton';
import EventParticipantTable from '../../components/EventParticipantTable';
import { eventService } from '../../service/firebase/eventService';
import { useQuery } from '@tanstack/react-query';
import { downloadEncryptedExcel } from '../../service/encryptedExportService';

export interface EventStatsItem {
  count: number;
  startDate: Timestamp;
  endDate: Timestamp;
  title: string;
}

interface EventContentProps {
  events: Event[];
  loading: boolean;
  error: string | null;
  addEvent: (event: Event) => Promise<void>;
  updateEvent: (event: Event) => Promise<void>;
  deleteEvent: (eventId: string) => Promise<void>;
}

const EventContent = ({
  events,
  loading,
  error,
  addEvent,
  updateEvent,
  deleteEvent,
}: EventContentProps) => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [dialogMode, setDialogMode] = useState<DialogMode>(DialogMode.CREATE);
  const [selectedEvent, setSelectedEvent] = useState<Event | undefined>(undefined);
  const [independentUsers, setIndependentUsers] = useState<User[]>([]);
  const [enterpriseUsers, setEnterpriseUsers] = useState<User[]>([]);
  const [enterprises, setEnterprises] = useState<Enterprise[]>([]);
  const [uniqueEventIds, setUniqueEventIds] = useState<string[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<string>('all');
  const [eventStats, setEventStats] = useState<{ [date: string]: { [eventId: string]: EventStatsItem } }>({});
  const [participantsData, setParticipantsData] = useState<EventSignUp[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Add these to your component state
  const getOneMonthAgo = () => {
    const date = new Date();
    date.setMonth(date.getMonth() - 1);
    return date.toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  const getTodayDate = () => {
    return new Date().toISOString().split('T')[0]; // Format: YYYY-MM-DD
  };

  const [startDate, setStartDate] = useState<string>(getOneMonthAgo());
  const [endDate, setEndDate] = useState<string>(getTodayDate());
  const [hideEndedEvents, setHideEndedEvents] = useState<boolean>(false);

  // 이벤트 참여 목록 조회
  const { data: eventSignUps = [], isLoading: isLoadingSignUps, error: errorSignUps } = useQuery({
    queryKey: ['eventSignUps', startDate, endDate, hideEndedEvents],
    queryFn: () => eventService.getEventSignUps(startDate, endDate, hideEndedEvents),
  });
  
  useEffect(() => {
    const db = getFirestore();
    const usersRef = collection(db, USER_COLLECTION);
    const q = query(usersRef, orderBy('createdAt', 'desc'));

    onSnapshot(q, (snapshot) => {
      const i_users: User[] = [];
      const e_users: User[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as User;
        if (data.uid?.length > 0 && data.displayName?.length > 0) {
          if (data.isEnterpriseUser && data.enterpriseId) {
            e_users.push(data);
          } else {
            i_users.push(data);
          }
        }
      });

      setIndependentUsers(i_users);
      setEnterpriseUsers(e_users);
    });

    const enterprisesRef = collection(db, ENTERPRISE_COLLECTION);
    const eq = query(enterprisesRef, orderBy('createdAt', 'desc'));

    onSnapshot(eq, (snapshot) => {
      const list: Enterprise[] = [];

      snapshot.forEach(doc => {
        const data = doc.data() as Enterprise;
        list.push(data);
      });

      setEnterprises(list);
    });
  }, []);

  useEffect(() => {
    setUniqueEventIds(() => {
      const ids = events.map(e => e.eventId).filter((id): id is string => !!id);
      return Array.from(new Set(ids));
    });
  }, [events]);

  useEffect(() => {
    let filteredSignUps;
    if (selectedEventId === 'all') {
      filteredSignUps = eventSignUps;
    } else {
      filteredSignUps = eventSignUps.filter(signUp => signUp.eventId === selectedEventId);
    }
    setParticipantsData(filteredSignUps);
    // Group by date and eventId, counting the number of signUps
    const groupedStats: { [date: string]: { [eventId: string]: EventStatsItem } } = {};
    filteredSignUps.forEach(signUp => {
      const date = signUp.timestamp.toString() || 'unknown';
      const eventId = signUp.eventId || 'unknown';

      // Find the event details
      const event = events.find(e => e.eventId === eventId);

      if (!groupedStats[date]) {
        groupedStats[date] = {};
      }
      if (!groupedStats[date][eventId]) {
        groupedStats[date][eventId] = {
          count: 0,
          startDate: event?.startDate || Timestamp.now(),
          endDate: event?.endDate || Timestamp.now(),
          title: event?.title || eventId,
        };
      }
      
      groupedStats[date][eventId].count += 1;
    });
    setEventStats(groupedStats);
  }, [eventSignUps, selectedEventId, events]);

  const handleAddEvent = () => {
    setDialogMode(DialogMode.CREATE);
    setSelectedEvent(undefined);
    setIsDialogOpen(true);
  };

  const handleEditEvent = (event: Event) => {
    setDialogMode(DialogMode.EDIT);
    setSelectedEvent(event);
    setIsDialogOpen(true);
  };

  const handleSaveEvent = async (event: Event) => {
    try {
      if (dialogMode === DialogMode.EDIT) {
        await updateEvent(event);
      } else {
        await addEvent(event);
      }
      handleCloseDialog();
    } catch (error) {
      alert('이벤트 저장에 실패했습니다.');
    }
  };

  const handleDeleteEvent = async (eventId: string) => {
    try {
      const confirmed = window.confirm('정말로 이 이벤트를 삭제하시겠습니까?');
      if (confirmed) {
        await deleteEvent(eventId);
        handleCloseDialog();
      }
    } catch (error) {
      alert('이벤트 삭제에 실패했습니다.');
    }
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedEvent(undefined);
  };

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  if (loading) {
    return (
      <AnimatedBox>
        <PageTitle>이벤트 관리</PageTitle>
        <LoadingState />
      </AnimatedBox>
    );
  }

  const handleSelectEventFilter = (eventId: string): void => {
    setSelectedEventId(eventId);
  }

  const handleExport = async (): Promise<void> => {
    if (participantsData.length === 0) {
      alert('내보낼 참가자 데이터가 없습니다.');
      return;
    }

    const exportData = participantsData.map(signUp => ({
      '이벤트 ID': signUp.eventId,
      '이름': signUp.name,
      '사용자 ID': signUp.uid,
      '이메일': signUp.email,
      '전화번호': signUp.phone,
      '마케팅 동의': signUp.consentMarketing ? '동의' : '미동의',
      '이벤트 타입': signUp.eventType,
      '참여시각': signUp.timestamp 
        ? signUp.timestamp.toDate().toLocaleString('ko-KR')
        : '-',
    }));

    setIsExporting(true);
    try {
      await downloadEncryptedExcel(exportData, '참가자 목록', '참가자목록');
    } catch (error) {
      console.error('Encrypted participant export failed:', error);
      alert('암호화 ZIP 생성에 실패했습니다. 잠시 후 다시 시도해주세요.');
    } finally {
      setIsExporting(false);
    }
};

  return (
    <AnimatedBox>
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'row', 
        alignItems: 'center', 
        justifyContent: 'space-between',
      }}>
        <PageTitle>이벤트 관리</PageTitle>
        <AddEventButton title='새 이벤트 추가' onAddClick={handleAddEvent} />
      </Box>

      {error && (
        <Box sx={{ mb: 3, color: 'error.main' }}>
          {error}
        </Box>
      )}

      <Tabs value={activeTab} onChange={handleTabChange} sx={{ mb: 2 }}>
        <Tab label="이벤트 목록" />
        <Tab label="참여자 목록" />
      </Tabs>

      {activeTab === 0 && (
        // 이벤트 목록 탭
        <Box>
          {events.length === 0 ? (
            <EmptyState message="아직 등록된 이벤트가 없습니다." />
          ) : (
            <EventTable
              data={events}
              onEventSelect={handleEditEvent}
            />
          )}
        </Box>
      )}

      {activeTab === 1 && (
        // 참여자 목록 탭
        <Box>
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              maxWidth: 500,
              flexDirection: { xs: 'column', sm: 'row' }
            }}
          >
            <EventFilter
              selectedEventId={selectedEventId}
              onEventChange={handleSelectEventFilter}
              uniqueEventIds={uniqueEventIds}
            />
            <ExportExcelButton onClick={handleExport} loading={isExporting} />
          </Box>

          {/* Date filters and checkbox */}
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              mb: 2,
              alignItems: 'center',
              flexWrap: 'wrap',
            }}
          >
            <TextField
              label="시작 날짜"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
            <TextField
              label="종료 날짜"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              size="small"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={hideEndedEvents}
                  onChange={(e) => setHideEndedEvents(e.target.checked)}
                />
              }
              label="종료된 이벤트 숨기기"
            />
          </Box>

          <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}>
            <EventSummaryTable
              eventStats={eventStats}
              selectedEventId={selectedEventId}
            />
            <EventParticipantTable
              data={participantsData}
            />
          </Box>
        </Box>
      )}

      {isDialogOpen && (
        <EventDialog 
          open={isDialogOpen}
          mode={dialogMode}
          event={selectedEvent}
          onClose={handleCloseDialog}
          onSave={handleSaveEvent}
          onDelete={handleDeleteEvent}
          i_users={independentUsers}
          e_users={enterpriseUsers}
          ent={enterprises}
        />
      )}
    </AnimatedBox>
  );
};

export default EventContent;
